import { Archive, Building2, LayoutDashboard, Mail, Pencil, Undo2, Users } from 'lucide-react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useOutletContext, useParams, useSearchParams } from 'react-router';
import { EntityFacts, EntityHead, RelatedSection } from '@/components/entity-sheet/EntityParts';
import { EntitySheet } from '@/components/entity-sheet/EntitySheet';
import { Button } from '@/components/ui/button';
import { Skeleton, SkeletonRegion } from '@/components/ui/skeleton';
import { formatDate } from '@/lib/intl';
import { cn } from '@/lib/utils';
import { placeBasePath, sectionPath } from '../place/place';
import { PlaceIcon } from '../ui/PlaceIcon';
import { StateMessage } from '../ui/StateMessage';
import type { OrganizationRow, OrganizationsContext } from './OrganizationsPage';

/**
 * An organization's card (decision 101), at a tenant's Organizzazioni: its state and people, the
 * acts the row offers (rename, archive, restore, «Amministra»), and its own sections as links. The
 * address is the one today's operator page used for one organization, so old links still land on
 * it. It reads from the list it was opened from.
 */
export function OrganizationSheet() {
  const { t } = useTranslation('administration');
  const context = useOutletContext<OrganizationsContext>();
  const { place, items, loading } = context;
  const { organizationId = '' } = useParams();
  const [params] = useSearchParams();
  const titleId = useId();
  const query = params.size > 0 ? `?${params.toString()}` : '';
  const listPath = sectionPath(place, 'organizations');
  const back = `${listPath}${query}`;
  const index = items.findIndex((row) => row.id === organizationId);
  const row = index >= 0 ? items[index] : undefined;
  const neighbour = (other: OrganizationRow | undefined, key: 'previous' | 'next') => {
    if (other === undefined) return null;
    return {
      to: `${listPath}/${other.id}${query}`,
      label: t(`organizations.card.${key}`, { name: other.name }),
    };
  };

  return (
    <EntitySheet
      titleId={titleId}
      back={back}
      backLabel={t('organizations.card.back')}
      closeLabel={t('organizations.card.close')}
      previous={neighbour(items[index - 1], 'previous')}
      next={index >= 0 ? neighbour(items[index + 1], 'next') : null}
    >
      {row !== undefined ? (
        <OrganizationCard row={row} titleId={titleId} context={context} />
      ) : (
        <GoneOrLoading loading={loading} titleId={titleId} back={back} />
      )}
    </EntitySheet>
  );
}

function GoneOrLoading({
  loading,
  titleId,
  back,
}: {
  readonly loading: boolean;
  readonly titleId: string;
  readonly back: string;
}) {
  const { t } = useTranslation('administration');
  if (loading) {
    return (
      <div>
        <h2 id={titleId} className="sr-only">
          {t('common.loading')}
        </h2>
        <SkeletonRegion label={t('common.loading')} className="flex flex-col gap-3">
          <Skeleton className="h-12 w-60" />
          <Skeleton className="h-32 w-full" />
        </SkeletonRegion>
      </div>
    );
  }
  return (
    <div>
      <h2 id={titleId} className="sr-only">
        {t('organizations.card.gone.title')}
      </h2>
      <StateMessage
        role="alert"
        icon={Building2}
        title={t('organizations.card.gone.title')}
        text={t('organizations.card.gone.text')}
        action={
          <Button asChild variant="outline">
            <Link to={back}>{t('organizations.card.back')}</Link>
          </Button>
        }
      />
    </div>
  );
}

function CardActions({
  row,
  acts,
  onRename,
  busy,
}: {
  readonly row: OrganizationRow;
  readonly acts: OrganizationsContext['acts'];
  readonly onRename: (row: OrganizationRow) => void;
  readonly busy: boolean;
}) {
  const { t } = useTranslation('administration');
  const archived = row.status === 'archived';
  const here = { kind: 'organization' as const, id: row.id };
  return (
    <>
      {!archived && row.can.administer ? (
        <Button asChild size="sm">
          <Link to={placeBasePath(here)}>{t('organizations.actions.administer')}</Link>
        </Button>
      ) : null}
      {row.can.rename ? (
        <Button variant="outline" size="sm" onClick={() => onRename(row)}>
          <Pencil />
          {t('organizations.actions.rename')}
        </Button>
      ) : null}
      {row.can.archive ? (
        <Button variant="outline" size="sm" disabled={busy} onClick={() => acts.archive(row)}>
          <Archive />
          {t('organizations.actions.archive')}
        </Button>
      ) : null}
      {archived && row.can.restore ? (
        <Button variant="outline" size="sm" disabled={busy} onClick={() => acts.restore(row)}>
          <Undo2 />
          {t('organizations.actions.restore')}
        </Button>
      ) : null}
    </>
  );
}

function OrganizationCard({
  row,
  titleId,
  context,
}: {
  readonly row: OrganizationRow;
  readonly titleId: string;
  readonly context: OrganizationsContext;
}) {
  const { t } = useTranslation('administration');
  const { detail, acts, onRename } = context;
  const archived = row.status === 'archived';
  const busy = acts.isPending(`archive:${row.id}`) || acts.isPending(`restore:${row.id}`);
  const here = { kind: 'organization' as const, id: row.id };
  const sections = [
    { key: 'overview', icon: LayoutDashboard, label: t('organizations.card.sections.overview') },
    { key: 'people', icon: Users, label: t('organizations.card.sections.people') },
    { key: 'invitations', icon: Mail, label: t('organizations.card.sections.invitations') },
  ] as const;

  return (
    <div className="flex flex-col gap-4">
      <EntityHead
        titleId={titleId}
        kind={t('organizations.kind')}
        title={row.name}
        lead={<PlaceIcon icon={Building2} muted={archived} />}
        status={
          <>
            <span className={cn('status-badge', !archived && 'success')}>
              {t(`organizations.status.${row.status}`)}
            </span>
            {archived && row.archivedAt !== null ? (
              <span className="entity-when">
                {t('organizations.archivedOn', { date: formatDate(row.archivedAt) })}
              </span>
            ) : null}
          </>
        }
        actions={<CardActions row={row} acts={acts} onRename={onRename} busy={busy} />}
      />
      <EntityFacts
        facts={[
          { label: t('organizations.card.facts.tenant'), value: detail.place.name },
          {
            label: t('organizations.card.facts.people'),
            value: t('organizations.peopleCount', { count: row.people }),
          },
          {
            label: t('organizations.card.facts.invitations'),
            value: archived
              ? t('organizations.invitationsOnHold', { count: row.pendingInvitations })
              : t('organizations.invitations', { count: row.pendingInvitations }),
          },
        ]}
      />
      {!archived && row.can.administer ? (
        <RelatedSection
          id={`${titleId}-sections`}
          title={t('organizations.card.sectionsTitle')}
          count={sections.length}
          rows={sections.map((section) => {
            const Icon = section.icon;
            return {
              key: section.key,
              to: sectionPath(here, section.key),
              label: section.label,
              lead: (
                <Icon aria-hidden="true" className="text-muted-foreground size-4.5 flex-none" />
              ),
            };
          })}
        />
      ) : null}
    </div>
  );
}
